import moment from 'moment';

/**
 * Dev-only sample data for the instrument create flows.
 *
 * Filling ~35 fields across six screens by hand to test one change is the
 * slowest part of working on these forms. Each screen passes its mock to
 * `InstrumentActions`, which renders a Prepopulate button — but only in dev
 * (see `isDev`) and only while creating, never when editing a real record.
 *
 * The data is a coherent imaginary issue rather than filler: a West African
 * toll-road concession raising money on the BRVM. Figures are internally
 * consistent (coupon matches the key facts, allocations total 100) so the
 * screens are exercised the way a real one would be.
 *
 * Anything backed by a live query — issuer, exchange, sector, topics — cannot
 * be a hard-coded id, so those come from the screen's own loaded options via
 * `generalDetailsMock`.
 */

// Real objects already in the dev bucket, so previews and links resolve.
const DEV_BUCKET =
  'https://dabafinancialinstruments-nonprod.s3.us-east-1.amazonaws.com/bonds';
const SAMPLE_ICON = `${DEV_BUCKET}/daba-finance-invest-africa-1787054112176-blackpng`;
const SAMPLE_PDF = `${DEV_BUCKET}/daba-finance-invest-africa-1787058368091-lorem_ipsumpdf`;

export const SAMPLE_IMAGE_URL = SAMPLE_ICON;

const days = n => moment().add(n, 'days');
const years = n => moment().add(n, 'years');

// ── Step 1 — shared base instrument ──────────────────────────────────────────

// Issuer, exchange, sector and topics are ids from live queries, so the screen
// supplies whichever of them it has loaded.
export const generalDetailsMock = ({
  issuer,
  exchange,
  sectorIndustry,
  topics,
} = {}) => ({
  name: 'Abidjan Toll Road Concession 2031',
  description:
    'A 12-year senior secured issue financing the Abidjan–Grand-Bassam toll road extension. Proceeds fund construction and a debt service reserve, with repayment carried by toll receipts under a concession running to 2044.',
  issuerType: 'CORPORATION',
  listingType: 'LISTED',
  currency: 'XOF',
  language: 'EN',
  shariaCompliant: false,
  ...(issuer && {issuer}),
  ...(exchange && {exchange}),
  ...(sectorIndustry && {sectorIndustry}),
  ...(topics?.length && {topics}),
});

export const TRADING_MOCK = {
  tradeable: true,
  fractionalShares: false,
  forDabaPro: false,
  enabledForFreeSimulation: false,
  useInventoryForTrade: true,
  minTradeableUnits: 10,
  maxTradeableUnits: 5000,
  orderExpirationLimit: 30,
  allocatedUnits: 250000,
};

export const KEY_FACTS_MOCK = {
  keyFacts: [
    {name: 'Coupon', value: '7.25% annual'},
    {name: 'Tenor', value: '12 years'},
    {name: 'Rating', value: 'BBB (stable outlook)'},
    {name: 'Security', value: 'Senior secured on toll receipts'},
    {name: 'Minimum investment', value: 'XOF 100,000'},
  ],
};

export const DISCLOSURES_MOCK = {
  disclosures: [
    // Statements are rich text (the editor is Quill), so the prefill is HTML — it is the
    // only way filling the form exercises what an operator actually authors.
    {
      title: 'Capital at risk',
      description:
        '<p>The value of your investment <strong>can fall as well as rise</strong>. You may get back less than you invest, and you could lose the full amount.</p>',
      mandatory: true,
    },
    {
      title: 'Past performance is not a guide to future returns',
      description:
        '<p>Returns shown are historical and net of fees unless stated otherwise. They <em>do not</em> predict, and are not a promise of, future performance.</p>',
      mandatory: true,
    },
    {
      title: 'Read the offer documents',
      description:
        '<p>This summary is not a substitute for:</p><ul><li>the prospectus</li><li>the fact sheet</li></ul><p>Read them in full before investing.</p>',
      mandatory: false,
    },
  ],
};

export const DOCUMENTS_MOCK = {
  documents: [
    {name: 'Information memorandum', fileLink: SAMPLE_PDF},
    {name: 'Trust deed', fileLink: SAMPLE_PDF},
  ],
};

// ── Step 2 — bond ────────────────────────────────────────────────────────────

export const BOND_DETAILS_MOCK = {
  ticker: 'ABTR31',
  isin: 'CI0000000451',
  type: 'CORPORATE',
  tenor: 12,
  couponRate: 7.25,
  couponFrequency: 'SEMI_ANNUAL',
  yieldToMaturity: 7.6,
  faceValue: 10000,
  currentMarketPrice: 9850,
  issuanceVolume: 25000000000,
  managementFee: 1.5,
  rating: 'BBB',
  callable: false,
  gracePeriodPrincipal: 24,
  gracePeriodInterest: 6,
  issuanceDate: days(-30),
  maturityDate: years(12),
  nextCouponDate: days(150),
  nextCallDate: years(5),
};

export const BOND_OFFERING_MOCK = {
  parties: [
    {
      role: 'LEAD_ARRANGER',
      name: 'Société Générale Côte d’Ivoire',
      website: 'https://societegenerale.ci',
      description:
        'Lead arranger and bookrunner for the issue, responsible for structuring and placement.',
      logo: SAMPLE_ICON,
    },
    {
      role: 'REGISTRAR',
      name: 'BRVM Central Depository',
      website: 'https://www.brvm.org',
      description: 'Maintains the register of holders and settles coupons.',
    },
  ],
  proceedDescription:
    'Proceeds fund the second carriageway of the Abidjan–Grand-Bassam toll road, the associated interchanges, and a six-month debt service reserve.',
  allocations: [
    {allocationCategory: 'Construction works', percentageValue: 65},
    {allocationCategory: 'Land acquisition', percentageValue: 15},
    {allocationCategory: 'Debt service reserve', percentageValue: 12},
    {allocationCategory: 'Fees and expenses', percentageValue: 8},
  ],
};

// ── Step 2 — mutual fund ─────────────────────────────────────────────────────

export const FUND_OVERVIEW_MOCK = {
  ticker: 'DWAGF',
  isin: 'CI0000000892',
  type: 'BALANCED',
  currentNetAssetValue: 12450,
  netAssetValueAtInception: 10000,
  currentNavDate: moment(),
  inceptionDate: years(-4),
  allowsAutoInvest: true,
  countries: ['CI', 'SN'],
};

export const FUND_DETAILS_MOCK = {
  name: 'Aminata Diallo',
  description:
    'Twenty years managing West African balanced mandates, previously head of fixed income at a regional asset manager.',
  website: 'https://example.com/fund-manager',
  investmentStrategy:
    'A balanced mandate holding regional blue-chip equities alongside sovereign and high-grade corporate debt, rebalanced quarterly to a 60/40 target.',
  bestSuitedFor:
    'Investors with a five-year horizon who want equity participation with the volatility dampened by a fixed income sleeve.',
  assetAllocation: [
    {assetClass: 'Equities', percent: 55},
    {assetClass: 'Bonds', percent: 35},
    {assetClass: 'Cash', percent: 10},
  ],
};

export const FUND_INVESTMENT_MOCK = {
  projectedAnnualReturn: 9.5,
  investmentHorizon: '5 years',
  valuationFrequency: 'DAILY',
  settlementDaysOnRedemption: 3,
  benefits: [
    {benefit: 'Diversified across the eight WAEMU markets in a single holding'},
    {benefit: 'Quarterly rebalancing keeps the risk profile steady'},
    {benefit: 'No entry fee on subscriptions above XOF 1,000,000'},
  ],
  trailingReturns: [
    {period: 'ONE_MONTH', metric: 'RETURN', value: 1.2},
    {period: 'ONE_YEAR', metric: 'RETURN', value: 11.4},
    {period: 'THREE_YEAR', metric: 'CAGR', value: 8.9},
  ],
  calendarReturns: [
    {year: 2023, metric: 'RETURN', value: 7.8},
    {year: 2024, metric: 'RETURN', value: 12.1},
  ],
};

// ── Step 2 — IPO ─────────────────────────────────────────────────────────────

export const IPO_DETAILS_MOCK = {
  ticker: 'ABPRT',
  isin: 'CI0000000733',
  offerPrice: 8000,
  priceRangeLow: 7500,
  priceRangeHigh: 8500,
  totalSharesOnOffer: 30000000,
  ipoStatus: 'UPCOMING',
  exclusiveOnDaba: true,
  fullAllocationLikely: false,
  offerAnnouncementDate: days(-7),
  subscriptionStartDate: days(3),
  subscriptionEndDate: days(24),
  allocationAnnouncementDate: days(31),
  refundDate: days(34),
  expectedListingDate: days(40),
  sellUnlockDate: days(70),
  highlights: [
    {highlight: 'Backed by a 25-year port concession running to 2051'},
    {highlight: 'Container volumes up 14% year on year'},
    {highlight: 'Dividend policy targets a 40% payout from first full year'},
  ],
};

export const IPO_OFFERING_MOCK = {
  parties: [
    {
      role: 'ISSUING_HOUSE',
      name: 'Hudson & Cie',
      website: 'https://example.com/hudson',
      description:
        'Issuing house coordinating the offer, allocation and refunds.',
      logo: SAMPLE_ICON,
    },
    {
      role: 'UNDERWRITER',
      name: 'Ecobank Capital',
      website: 'https://ecobank.com',
      description: 'Underwrites the retail tranche of the offer.',
    },
  ],
  proceedDescription:
    'Proceeds fund a third container berth, two ship-to-shore cranes, and the repayment of a bridge facility drawn during the terminal expansion.',
  allocations: [
    {allocationCategory: 'Berth construction', percentageValue: 45},
    {allocationCategory: 'Crane and equipment purchase', percentageValue: 30},
    {allocationCategory: 'Bridge facility repayment', percentageValue: 20},
    {allocationCategory: 'Offer expenses', percentageValue: 5},
  ],
};

// ── Step 2 — public equity ───────────────────────────────────────────────────

export const EQUITY_DETAILS_MOCK = {
  ticker: 'SNTS',
  isin: 'CI0000000121',
  listedAt: years(-6),
  dividendYield: 4.2,
  amountPerShare: 320,
  exDate: days(-45),
  lastDate: days(-30),
  nextDate: days(135),
  lastPrice: 7620,
  openingPrice: 7500,
  closingPrice: 7620,
  highPrice: 7700,
  lowPrice: 7480,
  volume: 128400,
};
